import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, MessageCircle, Play } from "lucide-react";
import { WA_DEFAULT } from "@/lib/site";
import { REELS } from "@/lib/content";
import { StoryViewer } from "./StoryViewer";

// Hero background clips: short muted cuts from the club's Instagram reels (reel = story opened on click)
const CLIPS = [
  { src: "/reels/hero/h1.mp4", reel: "DcrAvJcNTEI" },
  { src: "/reels/hero/h2.mp4", reel: "DcF3vRJtSC6" },
  { src: "/reels/hero/h3.mp4", reel: "DaHogi7N7Of" },
  { src: "/reels/hero/h4.mp4", reel: "DaKwrlBNdkr" },
];

const fade = (d) => ({ initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: d, ease: [0.22, 1, 0.36, 1] } });

const BgVideo = ({ i, onEnded }) => (
  <AnimatePresence initial={false}>
    <motion.video
      key={CLIPS[i].src}
      src={CLIPS[i].src}
      poster={i === 0 ? "/reels/hero/h1.jpg" : undefined}
      autoPlay
      muted
      playsInline
      preload="auto"
      onEnded={onEnded}
      data-testid="hero-bg-video"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.9 }}
      className="absolute inset-0 h-full w-full object-cover motion-reduce:hidden"
    />
  </AnimatePresence>
);

export const Hero = () => {
  const [i, setI] = useState(0);
  const [story, setStory] = useState(null);
  const next = () => setI((v) => (v + 1) % CLIPS.length);
  const openStory = () => setStory(Math.max(0, REELS.findIndex((r) => r.id === CLIPS[i].reel)));

  return (
    <section id="top" data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden bg-black lg:items-center">
      <img src="/reels/hero/h1.jpg" alt="Ormanda zıplayan FX enduro sürücüsü" className="absolute inset-0 h-full w-full object-cover" />
      <BgVideo i={i} onEnded={next} />
      <div className="absolute inset-0 bg-black/45" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/40 lg:bg-gradient-to-r lg:from-black/90 lg:via-black/40 lg:to-transparent" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-28 pt-32 md:px-8 lg:pb-16">
        <motion.p {...fade(0.05)} className="mb-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-neutral-200">
          <MapPin className="h-4 w-4 text-brand" /> Motosiklet Spor Kulübü · Çekmeköy / Ömerli
        </motion.p>
        <motion.h1 {...fade(0.15)} data-testid="hero-title" className="font-display text-6xl uppercase leading-[0.88] text-white sm:text-7xl md:text-8xl xl:text-[7.5rem] [&>span]:block [&>span]:whitespace-nowrap">
          <span>Parkur bizden.</span><span>Motor bizden.</span><span className="text-brand">Sen sadece sür.</span>
        </motion.h1>
        <motion.p {...fade(0.3)} className="mt-8 max-w-lg text-base leading-relaxed text-neutral-200 md:text-lg">
          Kendi parkurumuz ve doğası var; motor ve ekipman bizden. Eğitim, çocuk eğitimi, enduro tur ve kulüp üyeliği — hepsi tek yerde, FX Motor Sporları'nda.
        </motion.p>
        <motion.div {...fade(0.45)} className="mt-10 flex flex-col gap-3 sm:flex-row">
          <Link to="/rezervasyon" data-testid="hero-booking-btn" className="group flex items-center justify-center gap-3 bg-brand px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-brand">
            Tarih seç, yerini ayırt <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn" className="flex items-center justify-center gap-3 border border-white/40 bg-black/20 px-8 py-4 font-bold uppercase tracking-wider text-white backdrop-blur-sm transition-colors hover:border-white hover:bg-white hover:text-black">
            <MessageCircle className="h-5 w-5" /> WhatsApp
          </a>
        </motion.div>
      </div>

      <div className="absolute bottom-6 left-5 right-24 z-10 flex items-center justify-between gap-4 md:left-auto md:right-28">
        <div className="flex gap-1.5" data-testid="hero-clip-dots">
          {CLIPS.map((c, k) => (
            <button key={c.src} onClick={() => setI(k)} aria-label={`Video ${k + 1}`} data-testid={`hero-clip-dot-${k}`} className={`h-1 transition-all ${k === i ? "w-10 bg-brand" : "w-5 bg-white/40 hover:bg-white"}`} />
          ))}
        </div>
        <button onClick={openStory} data-testid="hero-watch-story-btn" className="flex items-center gap-2 border border-white/30 bg-black/30 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white backdrop-blur-sm transition-colors hover:bg-white hover:text-black">
          <Play className="h-4 w-4" /> Sesli izle
        </button>
      </div>
      {story !== null && <StoryViewer items={REELS} start={story} onClose={() => setStory(null)} onSeen={() => {}} />}
    </section>
  );
};
